/**
 * Gemeinsame Regeln für „Abschnitt entfernen“ und neue Links — gelten in der
 * Anzeige (abschnitte.ts, formatieren.ts, DOM) und im gespeicherten HTML
 * (anwenden.ts, Quelltext).
 * Keine Abhängigkeiten, damit beide Seiten sie ohne Kreis einbinden können.
 */

/**
 * Elemente, die man samt Gestaltung aus der Mail nehmen kann: Absatz,
 * Überschrift, Liste, Zelle, Zeile, Kasten (Tabelle), Bereich.
 */
export const ABSCHNITT_TAGS: ReadonlySet<string> = new Set([
  'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'dl', 'blockquote', 'div', 'table', 'tr', 'td', 'th',
  'center', 'section', 'article', 'header', 'footer', 'main', 'figure', 'pre',
]);

/** Längster Text eines Abschnitts (Zeichen) — passt in die Prüfung der Datenbank. */
export const ABSCHNITT_TEXT_MAX = 20000;
export const ABSCHNITT_BILDER_MAX = 30;
export const ABSCHNITT_BILD_MAX = 600;

/** Stile, die Text unsichtbar machen (Vorschautext, Platzhalter-Zeilen). */
export const VERSTECKT_STIL = /display\s*:\s*none|mso-hide\s*:\s*all|visibility\s*:\s*hidden|(^|[;\s])(max-height|height)\s*:\s*0(?![.\d])|(^|[;\s])font-size\s*:\s*0(px)?\s*(;|$)|opacity\s*:\s*0(?![.\d])/i;

/** Der Abmelde-Link ist Pflicht — Abschnitte mit ihm lassen sich nicht entfernen. */
export const ABMELDE = /unsubscribe/i;

/** Wie ein Abschnitt in der Oberfläche heißt. */
export function abschnittArt(tag: string): string {
  switch (tag) {
    case 'p': return 'Absatz';
    case 'h1': case 'h2': case 'h3': case 'h4': case 'h5': case 'h6': return 'Überschrift';
    case 'li': return 'Listenpunkt';
    case 'ul': case 'ol': case 'dl': return 'Liste';
    case 'blockquote': return 'Zitat';
    case 'td': case 'th': return 'Spalte';
    case 'tr': return 'Zeile';
    case 'table': return 'Kasten';
    default: return 'Bereich';
  }
}

/**
 * Neue Links in einem Vorschlag („Text ändern“ → Link): nur https und mailto,
 * ohne Leerraum, Anführungszeichen oder Platzhalter. Die Werkstatt zeigt die
 * Adresse auf der Karte, übernommen wird erst nach dem Blick des Teams.
 */
export const LINK_NEU_MAX = 2000;
const LINK_NEU = /^(https:\/\/[^\s"'<>`\\{}]+|mailto:[^\s"'<>`\\{}@]+@[^\s"'<>`\\{}@]+)$/i;

export function neuerLinkErlaubt(href: string): boolean {
  const h = href.trim();
  if (h.length > LINK_NEU_MAX || !LINK_NEU.test(h)) return false;
  for (let i = 0; i < h.length; i++) if (h.charCodeAt(i) < 32 || h.charCodeAt(i) === 127) return false;
  if (/^https:/i.test(h)) {
    try {
      const u = new URL(h);
      return u.protocol === 'https:' && u.hostname.includes('.') && !u.username && !u.password;
    } catch { return false; }
  }
  return true;
}

/**
 * Bedingte Kommentare (`<!--[if mso]> … <![endif]-->`) müssen im
 * herausgenommenen Stück paarweise vorkommen — sonst bliebe in Outlook
 * ein offener Rest zurück.
 */
export function bedingungenAusgeglichen(quelltext: string): boolean {
  const auf = quelltext.match(/<!--\s*\[if\b/gi)?.length ?? 0;
  const zu = quelltext.match(/<!\s*\[endif\]/gi)?.length ?? 0;
  return auf === zu;
}
